import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Users, Sparkles, ArrowLeft } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Craftsmanship',
      description: 'Every piece is handcrafted with meticulous attention to detail and passion for perfection.'
    },
    {
      icon: Sparkles,
      title: 'Luxury',
      description: 'We use only the finest materials to create timeless pieces that exude elegance and sophistication.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Our commitment to quality ensures each creation becomes a cherished heirloom.'
    },
    {
      icon: Users,
      title: 'Personalization',
      description: 'We work closely with clients to bring their unique visions to life through custom commissions.'
    }
  ];

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Back Button - Aligned with logo position */}
        <div className="mb-8 flex justify-center relative z-[60]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[hsl(45,100%,70%)] transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to home
          </Link>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-20">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Where art meets
            <br />
            <span className="text-[hsl(45,100%,70%)]">extraordinary craftsmanship</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
            Artourius Design was born from a passion for creating unique, handcrafted sculptures that transcend ordinary decoration. Each piece tells a story of dedication, skill, and artistic vision.
          </p>
        </div>

        {/* Video Section */}
        <div className="mb-20">
          <div className="aspect-[21/9] rounded-2xl overflow-hidden bg-gray-900">
            <video
              src="/about_video.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                Founded in the heart of Switzerland, Artourius Design represents the pinnacle of luxury sculptural art. Our journey began with a simple belief: that everyday objects can be transformed into extraordinary works of art.
              </p>
              <p>
                Each piece in our collection is carefully conceived and meticulously crafted by master artisans who have dedicated their lives to perfecting their craft. We combine traditional techniques with contemporary design sensibilities to create sculptures that are both timeless and relevant.
              </p>
              <p>
                From our signature bird cage lightnings to our bold sculptural statements, every creation embodies our commitment to excellence and our passion for pushing the boundaries of what's possible in decorative art.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-white mb-6">Our Philosophy</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                We believe that true luxury lies not in mass production, but in the unique character of handcrafted pieces. Each sculpture in our collection is created in strictly limited editions, ensuring that our clients own something truly exclusive.
              </p>
              <p>
                Our materials are sourced from the finest suppliers around the world. Bronze, brass, precious metals, and carefully selected crystals come together under the skilled hands of our artisans to create pieces that will be treasured for generations.
              </p>
              <p>
                Beyond creating beautiful objects, we strive to forge lasting relationships with our clients. Whether you're selecting from our existing collections or commissioning a custom piece, we're dedicated to bringing your vision to life with uncompromising quality and service.
              </p>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-gray-900 rounded-2xl p-8 border border-gray-800 hover:border-[hsl(45,100%,70%)] transition-all duration-300 group"
                >
                  <div className="w-16 h-16 rounded-full bg-[hsl(45,100%,70%)]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="w-8 h-8 text-[hsl(45,100%,70%)]" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-12 text-center border border-gray-800">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to own a piece of art?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Explore our collections or get in touch to discuss a custom commission.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/sculpture"
              className="px-8 py-4 bg-[hsl(45,100%,70%)] text-black font-bold rounded-full hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 hover:scale-105"
            >
              View Collections
            </a>
            <a
              href="/contact"
              className="px-8 py-4 border-2 border-[hsl(45,100%,70%)] text-[hsl(45,100%,70%)] font-bold rounded-full hover:bg-[hsl(45,100%,70%)] hover:text-black transition-all duration-300 hover:scale-105"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;